export type ShortcutDef = {
  keys: string;
  label: string;
  scope: 'global' | 'editor';
  hideInHelp?: boolean;
};

export const SHORTCUTS = {
  undo: { keys: 'mod+z', label: 'Undo', scope: 'global' },
  redo: { keys: 'mod+shift+z', label: 'Redo', scope: 'global' },
  openHelp: { keys: 'shift+?', label: 'Keyboard shortcuts', scope: 'global' },
  exportAtlas: { keys: 'mod+e', label: 'Export atlas', scope: 'global' },
  cancelDraw: { keys: 'esc', label: 'Cancel draw / clear selection', scope: 'editor' },
  drawCrop: { keys: 'c', label: 'Draw crop region', scope: 'editor' },
  drawWatermark: { keys: 'w', label: 'Draw watermark region', scope: 'editor' },
  pickBgColor: { keys: 'b', label: 'Pick background color from image', scope: 'editor' },
  keyRegion: { keys: 'r', label: 'Magic wand (flood-fill region)', scope: 'editor' },
  eraseMode: { keys: 'e', label: 'Soft eraser', scope: 'editor' },
  removeSelectedRegion: { keys: 'backspace', label: 'Remove selected region', scope: 'editor' },
  autoDetectGrid: { keys: 'g', label: 'Auto-detect grid', scope: 'editor' },
  panMode: { keys: 'v', label: 'Pan mode (drag to move; scroll to zoom)', scope: 'editor' },
  resetViewport: { keys: '0', label: 'Reset zoom and pan', scope: 'editor' },
  toggleSnap: { keys: 's', label: 'Toggle snap to guidelines', scope: 'editor' },
  addVerticalGuideline: { keys: 'shift+v', label: 'Add vertical guideline', scope: 'editor' },
  addHorizontalGuideline: { keys: 'shift+h', label: 'Add horizontal guideline', scope: 'editor' },
  nudgeUp: { keys: 'up', label: 'Nudge selected target ↑ (1px) — Shift for 10px', scope: 'editor' },
  nudgeDown: { keys: 'down', label: 'Nudge ↓', scope: 'editor', hideInHelp: true },
  nudgeLeft: { keys: 'left', label: 'Nudge ←', scope: 'editor', hideInHelp: true },
  nudgeRight: { keys: 'right', label: 'Nudge →', scope: 'editor', hideInHelp: true },
  nudgeUpBig: { keys: 'shift+up', label: 'Nudge ↑ (10px)', scope: 'editor', hideInHelp: true },
  nudgeDownBig: { keys: 'shift+down', label: 'Nudge ↓ (10px)', scope: 'editor', hideInHelp: true },
  nudgeLeftBig: { keys: 'shift+left', label: 'Nudge ← (10px)', scope: 'editor', hideInHelp: true },
  nudgeRightBig: {
    keys: 'shift+right',
    label: 'Nudge → (10px)',
    scope: 'editor',
    hideInHelp: true,
  },
  selectRegion: { keys: 'm', label: 'Region-select tool (marquee)', scope: 'editor' },
  selectAllBoxes: { keys: 'mod+a', label: 'Select all grid cells', scope: 'editor' },
} as const satisfies Record<string, ShortcutDef>;

export type ShortcutId = keyof typeof SHORTCUTS;

if (import.meta.env.DEV) {
  const seen = new Map<string, string>();
  for (const [id, def] of Object.entries(SHORTCUTS)) {
    if (seen.has(def.keys)) {
      throw new Error(
        `Duplicate shortcut keys "${def.keys}" used by ${seen.get(def.keys)} and ${id}`,
      );
    }
    seen.set(def.keys, id);
  }
}
